import { cn } from '@/lib/utils';
import { Tooltip } from '@/libs/components/src/lib/Tooltip';
import { RadioGroup } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

type Props = {
  providers: any[];
  onChange: (provider: string) => void;
  value: string | null;
};

const ScheduleProviders: React.FC<Props> = ({ providers, onChange, value }) => {
  const providerOptions = providers?.concat({
    ID: 'no-preference',
    name: 'No preference? See all provider availability.',
  });

  return (
    <RadioGroup value={value} onChange={onChange} id="providers" className="mt-8">
      <RadioGroup.Label>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Select a provider</h3>
      </RadioGroup.Label>
      <div className="mt-4 flex flex-col gap-2.5">
        {providerOptions?.map((provider, idx) => (
          <RadioGroup.Option
            key={provider.ID || idx}
            value={provider.ID}
            className={({ checked, active }) =>
              cn(
                'portal-card relative flex cursor-pointer p-4 focus:outline-none transition-shadow',
                checked ? 'ring-2 ring-[var(--primary-color)]' : '',
                active ? 'ring-2 ring-[var(--primary-color)]' : '',
                provider.ID === 'no-preference' ? 'text-center' : ''
              )
            }
          >
            {({ checked }) => (
              <>
                <span className="flex flex-1">
                  {provider.ID === 'no-preference' ? (
                    <RadioGroup.Label
                      as="span"
                      className="text-sm font-semibold leading-6 text-gray-500 dark:text-gray-400 dark:text-gray-500 text-center w-full"
                    >
                      <span className="absolute inset-x-0 -top-px bottom-0" />
                      {provider.name}
                    </RadioGroup.Label>
                  ) : (
                    <div className="flex gap-x-3 items-center">
                      {provider.profileImage ? (
                        <img
                          className="h-11 w-11 flex-none rounded-xl bg-gray-50 object-cover"
                          src={provider.profileImage}
                          alt={provider.name}
                        />
                      ) : (
                        <span
                          className="inline-flex h-11 w-11 items-center justify-center rounded-xl flex-none text-white font-bold text-sm"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        >
                          {provider.name?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                      )}
                      <div className="min-w-0 flex-auto text-left">
                        <RadioGroup.Label
                          as="span"
                          className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                        >
                          <div className="flex flex-row gap-2 items-center">
                            <span>{provider.name}</span>
                            {!!provider.bio && (
                              <Tooltip
                                content={ReactDOMServer.renderToStaticMarkup(
                                  <div className="w-9/10 sm:w-64 md:w-96 flex flex-col gap-4">
                                    {provider.bio
                                      ?.split(/\n+/g)
                                      ?.map((line: string, idx2: number) => (
                                        <div key={idx2}>{line}</div>
                                      ))}
                                  </div>
                                )}
                              >
                                <InformationCircleIcon className="inline-block h-4 w-4 text-gray-400 dark:text-gray-500 cursor-pointer z-50 hover:text-gray-600" />
                              </Tooltip>
                            )}
                          </div>
                        </RadioGroup.Label>
                        <RadioGroup.Description
                          as="span"
                          className="mt-0.5 flex text-xs text-gray-400 dark:text-gray-500"
                        >
                          <span className="relative truncate">{provider.degree}</span>
                        </RadioGroup.Description>
                      </div>
                    </div>
                  )}
                </span>
                <CheckCircleIcon
                  className={cn(!checked ? 'invisible' : '', 'h-5 w-5 text-[var(--primary-color)]')}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};

export default ScheduleProviders;
